var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import { styled } from '@mui/material/styles';
import { MenuItem } from '../MenuItem';
const StyledChevronContainer = styled(ListItemIcon) `
    // need to increase specificity to override MUI styles
    &.MuiListItemIcon-root {
        min-width: 20px;
    }
`;
const StyledMenuItemInner = styled(Box) `
    display: flex;
    width: 100%;
`;
export const NestedMenuItem = (props) => {
    var _a;
    const { parentMenuOpen, label, icon, children, tabIndex: tabIndexProp, MenuProps, MenuItemBoxProps, onBlur, onFocus, onMouseEnter, onMouseLeave, onKeyDown } = props, other = __rest(props, ["parentMenuOpen", "label", "icon", "children", "tabIndex", "MenuProps", "MenuItemBoxProps", "onBlur", "onFocus", "onMouseEnter", "onMouseLeave", "onKeyDown"]);
    const containerRef = useRef(null);
    const menuContainerRef = useRef(null);
    const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
    // Check if any immediate children are active
    const isSubmenuFocused = () => {
        var _a, _b;
        if (!menuContainerRef.current) {
            return false;
        }
        const list = menuContainerRef.current.getElementsByTagName('ul')[0];
        const active = (_b = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.ownerDocument.activeElement) !== null && _b !== void 0 ? _b : null;
        for (const child of list.children) {
            if (child === active) {
                return true;
            }
        }
        return false;
    };
    const handleMouseEnter = (e) => {
        setIsSubMenuOpen(true);
        onMouseEnter === null || onMouseEnter === void 0 ? void 0 : onMouseEnter(e);
    };
    const handleMouseLeave = (e) => {
        setIsSubMenuOpen(false);
        onMouseLeave === null || onMouseLeave === void 0 ? void 0 : onMouseLeave(e);
    };
    const handleFocus = (e) => {
        if (e.target === containerRef.current) {
            setIsSubMenuOpen(true);
        }
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
    };
    const handleBlur = (e) => {
        e.stopPropagation();
    };
    const focusFirstChildInSubmenu = () => {
        if (!menuContainerRef.current) {
            return;
        }
        const list = menuContainerRef.current.getElementsByTagName('ul')[0];
        const firstChild = list.children[0];
        firstChild === null || firstChild === void 0 ? void 0 : firstChild.focus();
    };
    const handleKeyDown = (e) => {
        var _a, _b;
        if (e.key === 'Escape') {
            return;
        }
        const isSubmenuFocusedValue = isSubmenuFocused();
        if (isSubmenuFocusedValue) {
            e.stopPropagation();
        }
        const active = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.ownerDocument.activeElement;
        if (e.key === 'ArrowLeft' && isSubmenuFocusedValue) {
            (_b = containerRef.current) === null || _b === void 0 ? void 0 : _b.focus();
            setIsSubMenuOpen(false);
        }
        if (e.key === 'ArrowRight' && e.target === containerRef.current && e.target === active) {
            if (!menuContainerRef.current) {
                setIsSubMenuOpen(true);
                window.setTimeout(focusFirstChildInSubmenu);
                return;
            }
            focusFirstChildInSubmenu();
        }
        if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && !isSubmenuFocusedValue) {
            setIsSubMenuOpen(false);
        }
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
    };
    const handleMenuOnClose = (...args) => {
        var _a;
        setIsSubMenuOpen(false);
        (_a = MenuProps === null || MenuProps === void 0 ? void 0 : MenuProps.onClose) === null || _a === void 0 ? void 0 : _a.call(MenuProps, ...args);
    };
    const open = isSubMenuOpen && parentMenuOpen;
    // Root element must have a `tabIndex` attribute for keyboard navigation
    let tabIndex;
    if (!props.disabled) {
        tabIndex = tabIndexProp !== null && tabIndexProp !== void 0 ? tabIndexProp : -1;
    }
    return (_jsxs(MenuItem, Object.assign({ ref: containerRef, onFocus: handleFocus, onBlur: handleBlur, tabIndex: tabIndex, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, onKeyDown: handleKeyDown }, other, { children: [_jsxs(StyledMenuItemInner, Object.assign({}, MenuItemBoxProps, { children: [icon && _jsx(ListItemIcon, { children: icon }), _jsx(ListItemText, { sx: { mr: 2.5 }, children: label }), _jsx(StyledChevronContainer, { children: _jsx(ChevronRightIcon, {}) })] })), _jsx(Menu, Object.assign({ ref: menuContainerRef, anchorEl: containerRef.current, anchorOrigin: {
                    horizontal: 'right',
                    vertical: 'top'
                }, transformOrigin: {
                    horizontal: 'left',
                    vertical: 'top'
                }, 
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus: false, disableAutoFocus: true, disableEnforceFocus: true, disablePortal: true }, MenuProps, { onClose: handleMenuOnClose, open: open, 
                // Set pointer events to 'none' to prevent the invisible Popover div
                // from capturing events for clicks and hovers
                sx: Object.assign({ pointerEvents: 'none' }, MenuProps === null || MenuProps === void 0 ? void 0 : MenuProps.sx), MenuListProps: Object.assign(Object.assign({}, MenuProps === null || MenuProps === void 0 ? void 0 : MenuProps.MenuListProps), { onMouseLeave: () => {
                        setIsSubMenuOpen(false);
                    }, style: Object.assign({ pointerEvents: 'auto' }, (_a = MenuProps === null || MenuProps === void 0 ? void 0 : MenuProps.MenuListProps) === null || _a === void 0 ? void 0 : _a.style) }), children: children }))] })));
};
